/**
 * React Query Client Configuration
 * Module 07 - Dashboard UI
 * Section 08 - State Management
 * 
 * Configures React Query with optimal defaults for the dashboard
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Create and configure React Query client
 * 
 * Configuration:
 * - staleTime: Data considered fresh for 30 seconds
 * - gcTime: Unused data kept in cache for 5 minutes
 * - refetchOnWindowFocus: Disabled to prevent unnecessary refetches
 * - refetchOnMount: Enabled for latest data on component mount
 * - retry: 3 attempts with exponential backoff
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // How long data is considered fresh (no refetch needed)
      staleTime: 30 * 1000, // 30 seconds
      
      // How long unused data stays in cache
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      
      // Refetch on window focus
      refetchOnWindowFocus: false,
      
      // Refetch on component mount
      refetchOnMount: true,
      
      // Retry failed requests
      retry: 3,
      
      // Retry delay (exponential backoff)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry failed mutations
      retry: 1,
      
      // Retry delay for mutations
      retryDelay: 1000,
    },
  },
});

/**
 * Query key factory for consistent query keys
 * Helps with invalidation and caching
 */
export const queryKeys = {
  // Auth keys
  auth: {
    user: ['auth', 'user'] as const,
    validate: ['auth', 'validate'] as const,
  },
  
  // Models keys
  models: {
    all: ['models'] as const,
    detail: (id: string) => ['models', id] as const,
    storageUsage: ['storage-usage'] as const,
  },
  
  // Embeds keys
  embeds: {
    all: ['embeds'] as const,
    detail: (id: string) => ['embeds', id] as const,
    byModel: (modelId: string) => ['embeds', 'model', modelId] as const,
  },
} as const;
